/**
 * IndexedDB-based caching service for cookbook files
 * Enables offline access and instant subsequent loads for large ebooks
 */

const DB_NAME = 'cookbookCache';
const DB_VERSION = 1;
const STORE_NAME = 'cookbooks';
export const MAX_CACHE_SIZE = 1024 * 1024 * 1024; // 1GB limit

let db = null;

/**
 * Initialize the IndexedDB database
 */
export const initDB = () => {
  return new Promise((resolve, reject) => {
    if (db) {
      resolve(db);
      return;
    }

    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = () => {
      console.error('Failed to open IndexedDB:', request.error);
      reject(request.error);
    };

    request.onsuccess = () => {
      db = request.result;
      resolve(db);
    };

    request.onupgradeneeded = (event) => {
      const database = event.target.result;

      if (!database.objectStoreNames.contains(STORE_NAME)) {
        const store = database.createObjectStore(STORE_NAME, { keyPath: 'id' });
        store.createIndex('lastAccessedAt', 'lastAccessedAt', { unique: false });
        store.createIndex('cachedAt', 'cachedAt', { unique: false });
      }
    };
  });
};

/**
 * Get a cached cookbook by ID
 * @param {number|string} id - Cookbook ID
 * @returns {Promise<{blob: Blob, metadata: Object}|null>}
 */
export const getCachedCookbook = async (id) => {
  try {
    await initDB();

    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORE_NAME], 'readwrite');
      const store = transaction.objectStore(STORE_NAME);
      const request = store.get(Number(id));

      request.onsuccess = () => {
        const result = request.result;
        if (result) {
          // Update last accessed time
          result.lastAccessedAt = Date.now();
          store.put(result);

          resolve({
            blob: result.blob,
            metadata: {
              id: result.id,
              title: result.title,
              fileType: result.fileType,
              fileSize: result.fileSize,
              cachedAt: result.cachedAt,
              lastAccessedAt: result.lastAccessedAt
            }
          });
        } else {
          resolve(null);
        }
      };

      request.onerror = () => {
        console.error('Failed to get cached cookbook:', request.error);
        reject(request.error);
      };
    });
  } catch (error) {
    console.error('Error accessing cache:', error);
    return null;
  }
};

/**
 * Cache a cookbook file
 * @param {number|string} id - Cookbook ID
 * @param {Blob} blob - The file blob
 * @param {Object} metadata - Cookbook metadata (title, fileType, etc.)
 */
export const cacheCookbook = async (id, blob, metadata) => {
  try {
    await initDB();

    // Enforce storage limit before adding new item
    await enforceStorageLimit(blob.size);

    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORE_NAME], 'readwrite');
      const store = transaction.objectStore(STORE_NAME);

      const record = {
        id: Number(id),
        blob: blob,
        title: metadata.title || 'Untitled',
        fileType: metadata.fileType || 'unknown',
        fileSize: blob.size,
        cachedAt: Date.now(),
        lastAccessedAt: Date.now()
      };

      const request = store.put(record);

      request.onsuccess = () => {
        resolve(true);
      };

      request.onerror = () => {
        console.error('Failed to cache cookbook:', request.error);
        reject(request.error);
      };
    });
  } catch (error) {
    console.error('Error caching cookbook:', error);
    // Don't throw - caching failure shouldn't break the app
    return false;
  }
};

/**
 * Remove a specific cookbook from cache
 * @param {number|string} id - Cookbook ID
 */
export const removeCachedCookbook = async (id) => {
  try {
    await initDB();

    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORE_NAME], 'readwrite');
      const store = transaction.objectStore(STORE_NAME);
      const request = store.delete(Number(id));

      request.onsuccess = () => {
        resolve(true);
      };

      request.onerror = () => {
        console.error('Failed to remove cached cookbook:', request.error);
        reject(request.error);
      };
    });
  } catch (error) {
    console.error('Error removing from cache:', error);
    return false;
  }
};

/**
 * Get cache statistics
 * @returns {Promise<{totalSize: number, bookCount: number, books: Array}>}
 */
export const getCacheStats = async () => {
  try {
    await initDB();

    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORE_NAME], 'readonly');
      const store = transaction.objectStore(STORE_NAME);
      const request = store.getAll();

      request.onsuccess = () => {
        const books = request.result || [];
        const totalSize = books.reduce((sum, book) => sum + (book.fileSize || 0), 0);

        resolve({
          totalSize,
          maxSize: MAX_CACHE_SIZE,
          bookCount: books.length,
          books: books.map(book => ({
            id: book.id,
            title: book.title,
            fileType: book.fileType,
            fileSize: book.fileSize,
            cachedAt: book.cachedAt,
            lastAccessedAt: book.lastAccessedAt
          })).sort((a, b) => b.lastAccessedAt - a.lastAccessedAt)
        });
      };

      request.onerror = () => {
        console.error('Failed to get cache stats:', request.error);
        reject(request.error);
      };
    });
  } catch (error) {
    console.error('Error getting cache stats:', error);
    return { totalSize: 0, maxSize: MAX_CACHE_SIZE, bookCount: 0, books: [] };
  }
};

/**
 * Check if a cookbook is cached
 * @param {number|string} id - Cookbook ID
 * @returns {Promise<boolean>}
 */
export const isCookbookCached = async (id) => {
  try {
    await initDB();

    return new Promise((resolve) => {
      const transaction = db.transaction([STORE_NAME], 'readonly');
      const store = transaction.objectStore(STORE_NAME);
      const request = store.get(Number(id));

      request.onsuccess = () => {
        resolve(!!request.result);
      };

      request.onerror = () => {
        resolve(false);
      };
    });
  } catch (error) {
    return false;
  }
};

/**
 * Get cached status for multiple cookbooks
 * @param {Array<number|string>} ids - Array of cookbook IDs
 * @returns {Promise<Object>} - Map of id -> boolean
 */
export const getCachedStatusBulk = async (ids) => {
  try {
    await initDB();

    return new Promise((resolve) => {
      const transaction = db.transaction([STORE_NAME], 'readonly');
      const store = transaction.objectStore(STORE_NAME);
      const statusMap = {};
      let completed = 0;

      ids.forEach(id => {
        const request = store.get(Number(id));
        request.onsuccess = () => {
          statusMap[id] = !!request.result;
          completed++;
          if (completed === ids.length) {
            resolve(statusMap);
          }
        };
        request.onerror = () => {
          statusMap[id] = false;
          completed++;
          if (completed === ids.length) {
            resolve(statusMap);
          }
        };
      });

      if (ids.length === 0) {
        resolve(statusMap);
      }
    });
  } catch (error) {
    console.error('Error getting bulk cache status:', error);
    return {};
  }
};

/**
 * Enforce storage limit by removing least recently used items
 * @param {number} incomingSize - Size of new item being added
 */
export const enforceStorageLimit = async (incomingSize = 0) => {
  try {
    const stats = await getCacheStats();
    let currentSize = stats.totalSize;

    // If adding the new item would exceed limit, remove old items
    while (currentSize + incomingSize > MAX_CACHE_SIZE && stats.books.length > 0) {
      // Sort by lastAccessedAt ascending (oldest first)
      const sortedBooks = [...stats.books].sort((a, b) => a.lastAccessedAt - b.lastAccessedAt);
      const oldestBook = sortedBooks[0];

      if (oldestBook) {
        await removeCachedCookbook(oldestBook.id);
        currentSize -= oldestBook.fileSize;
        stats.books = stats.books.filter(b => b.id !== oldestBook.id);
      } else {
        break;
      }
    }
  } catch (error) {
    console.error('Error enforcing storage limit:', error);
  }
};

/**
 * Clear all cached cookbooks
 */
export const clearAllCache = async () => {
  try {
    await initDB();

    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORE_NAME], 'readwrite');
      const store = transaction.objectStore(STORE_NAME);
      const request = store.clear();

      request.onsuccess = () => {
        resolve(true);
      };

      request.onerror = () => {
        console.error('Failed to clear cache:', request.error);
        reject(request.error);
      };
    });
  } catch (error) {
    console.error('Error clearing cache:', error);
    return false;
  }
};

/**
 * Format bytes to human readable string
 * @param {number} bytes
 * @returns {string}
 */
export const formatBytes = (bytes) => {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

export default {
  initDB,
  getCachedCookbook,
  cacheCookbook,
  removeCachedCookbook,
  getCacheStats,
  isCookbookCached,
  getCachedStatusBulk,
  enforceStorageLimit,
  clearAllCache,
  formatBytes,
  MAX_CACHE_SIZE
};
